import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  Stack,
  Grid,
} from '@mui/material';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP, fpInputSx } from 'components/financepro';

const CONFIGURACION_DEFAULT = {
  plantillaPorDefecto: 'carta-profesional',
  leyendaPiePagina: '',
  mensajeGracias: 'Gracias por su preferencia',
  mostrarInfoEmpresa: true,
  nombreEmpresa: '',
  direccionEmpresa: '',
  telefonoEmpresa: '',
  emailEmpresa: '',
  rncEmpresa: '',
  mostrarObservaciones: true,
  mostrarDescuentos: true,
  mostrarImpuestos: true,
  mostrarSubtotalAntesDescuentos: true,
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const formatoFechaLargo = (f) => {
  if (!f) return '—';
  const d = new Date(f);
  return isNaN(d.getTime())
    ? f
    : d.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatoFechaCorto = (f) => {
  if (!f) return '—';
  const d = new Date(f);
  return isNaN(d.getTime())
    ? f
    : d.toLocaleDateString('es-DO', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const escHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * HTML de impresión carta 8.5x11 — estilo empresarial para cotizaciones.
 */
const generarHtmlImpresionCotizacion = ({
  numero,
  fecha,
  cliente,
  direccion,
  direccion_envio,
  telefono,
  items = [],
  subtotalAntesDescuentos = 0,
  totalDescuentos = 0,
  totalImpuestos = 0,
  total = 0,
  observaciones = '',
  nombreEmpresa = 'Empresa',
  direccionEmpresa = '',
  telefonoEmpresa = '',
  rncEmpresa = '',
  emailEmpresa = '',
  mostrarInfoEmpresa = true,
  leyendaPiePagina = '',
  mensajeGracias = '',
}) => {
  const itemsHtml =
    !items || items.length === 0
      ? `<tr><td colspan="6" style="text-align:center;padding:22px;color:#888;">Sin ítems</td></tr>`
      : items
          .map(
            (item, idx) => `
        <tr style="background:${idx % 2 === 0 ? '#ffffff' : '#f7f8fa'};">
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;font-size:12px;color:#555;">${escHtml(item.codigo || '—')}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;font-size:12px;color:#222;">${escHtml(item.descripcion || '—')}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:center;font-size:12px;">${parseFloat(item.cantidad || 0)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;">${money(item.precio)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;">${money(item.descuento)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;font-weight:700;">${money(item.subtotal)}</td>
        </tr>`
          )
          .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Cotización ${escHtml(numero || '')}</title>
  <style>
    @page { size: letter; margin: 16mm; }
    @media print {
      body { margin: 0 !important; padding: 0 !important; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    body {
      font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      color: #1a1f2c;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
      padding: 16mm;
      background: #fff;
    }
    .accent { height: 4px; background: #0E1F33; margin: 0 0 22px 0; }
    .meta-label {
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #6b7280;
      font-weight: 700;
      margin-bottom: 4px;
    }
    .section-title {
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: #0E1F33;
      font-weight: 700;
      margin: 0 0 8px 0;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }
    table.items { width: 100%; border-collapse: collapse; margin: 8px 0 0 0; }
    table.items th {
      background: #0E1F33;
      color: #fff;
      padding: 11px 8px;
      text-align: left;
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: 700;
    }
    .totales {
      width: 280px;
      margin-left: auto;
      margin-top: 22px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      overflow: hidden;
    }
    .totales .row {
      display: flex;
      justify-content: space-between;
      padding: 8px 14px;
      font-size: 12px;
      color: #4b5563;
      border-bottom: 1px solid #f0f1f3;
    }
    .totales .total {
      background: #0E1F33;
      color: #fff;
      font-size: 16px;
      font-weight: 700;
      padding: 12px 14px;
      display: flex;
      justify-content: space-between;
    }
    .footer {
      margin-top: 36px;
      padding-top: 14px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 11px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="accent"></div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:22px;">
    <tr>
      <td style="width:58%;vertical-align:top;padding:0;">
        ${
          mostrarInfoEmpresa
            ? `
          <div style="font-size:22px;font-weight:700;color:#0E1F33;letter-spacing:0.01em;margin:0 0 6px 0;">${escHtml(nombreEmpresa)}</div>
          ${direccionEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">${escHtml(direccionEmpresa)}</div>` : ''}
          ${telefonoEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">Tel: ${escHtml(telefonoEmpresa)}</div>` : ''}
          ${rncEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">RNC: ${escHtml(rncEmpresa)}</div>` : ''}
          ${emailEmpresa ? `<div style="font-size:12px;color:#4b5563;margin:2px 0;">${escHtml(emailEmpresa)}</div>` : ''}
        `
            : ''
        }
      </td>
      <td style="width:42%;vertical-align:top;text-align:right;padding:0;">
        <div class="meta-label">Documento</div>
        <div style="font-size:18px;font-weight:700;color:#0E1F33;letter-spacing:0.08em;margin-bottom:8px;">COTIZACIÓN</div>
        <div style="font-size:20px;font-weight:700;color:#111;margin-bottom:8px;">Nº ${escHtml(numero || '—')}</div>
        <div style="font-size:12px;color:#4b5563;">Fecha: ${escHtml(formatoFechaLargo(fecha))}</div>
      </td>
    </tr>
  </table>

  <div style="background:#f7f8fa;border:1px solid #e5e7eb;border-radius:6px;padding:14px 16px;margin-bottom:22px;">
    <div class="section-title" style="border:none;padding:0;margin-bottom:10px;">Cliente</div>
    <table style="width:100%;border-collapse:collapse;">
      <tr>
        <td style="width:50%;vertical-align:top;padding:0 12px 0 0;font-size:12px;color:#374151;">
          <div style="font-weight:700;color:#111;font-size:14px;margin-bottom:4px;">${escHtml(cliente || '—')}</div>
          ${direccion ? `<div style="margin:2px 0;">${escHtml(direccion)}</div>` : ''}
          ${telefono ? `<div style="margin:2px 0;">Tel: ${escHtml(telefono)}</div>` : ''}
        </td>
        <td style="width:50%;vertical-align:top;padding:0;font-size:12px;color:#374151;">
          ${
            direccion_envio
              ? `<div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#6b7280;font-weight:700;margin-bottom:4px;">Entrega / Envío</div>
                 <div>${escHtml(direccion_envio)}</div>`
              : ''
          }
        </td>
      </tr>
    </table>
  </div>

  <div class="section-title">Detalle</div>
  <table class="items">
    <thead>
      <tr>
        <th style="width:12%;">Código</th>
        <th style="width:38%;">Descripción</th>
        <th style="width:10%;text-align:center;">Cant.</th>
        <th style="width:13%;text-align:right;">Precio</th>
        <th style="width:13%;text-align:right;">Desc.</th>
        <th style="width:14%;text-align:right;">Subtotal</th>
      </tr>
    </thead>
    <tbody>${itemsHtml}</tbody>
  </table>

  <div class="totales">
    <div class="row"><span>Subtotal</span><span>${money(subtotalAntesDescuentos)}</span></div>
    ${
      Number(totalDescuentos) > 0
        ? `<div class="row"><span>Descuentos</span><span style="color:#b91c1c;">−${money(totalDescuentos)}</span></div>`
        : ''
    }
    <div class="row"><span>Impuestos</span><span>${money(totalImpuestos)}</span></div>
    <div class="total"><span>TOTAL</span><span>${money(total)}</span></div>
  </div>

  ${
    observaciones
      ? `<div style="margin-top:22px;">
          <div class="section-title">Observaciones</div>
          <div style="font-size:12px;color:#4b5563;white-space:pre-wrap;">${escHtml(observaciones)}</div>
        </div>`
      : ''
  }

  <div class="footer">
    ${mensajeGracias ? `<div style="font-weight:600;color:#0E1F33;margin-bottom:6px;">${escHtml(mensajeGracias)}</div>` : ''}
    ${leyendaPiePagina ? `<div>${escHtml(leyendaPiePagina)}</div>` : ''}
    <div style="margin-top:10px;">Este documento es una cotización y no constituye una factura fiscal.</div>
  </div>
</body>
</html>`;
};

/**
 * VistaCotizacion: presenta la cotización en pantalla con opciones de imprimir y enviar por email.
 * La configuración de impresión se obtiene del backend (tabla configuracion_factura, tipo COTIZACION).
 */
const VistaCotizacion = ({ open, onClose, cotizacionData = {}, autoPrint = false }) => {
  const { controller, showSuccess, showError } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const nombreEmpresaContexto =
    typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '';
  const direccionEmpresaContexto =
    typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
      ? direccionEmpresaCtx.trim()
      : '';
  const rncEmpresaContexto =
    typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';

  const printRef = useRef(null);
  const [dialogoEmailAbierto, setDialogoEmailAbierto] = useState(false);
  const [emailDestino, setEmailDestino] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [configuracion, setConfiguracion] = useState(CONFIGURACION_DEFAULT);
  const [configuracionGuardada, setConfiguracionGuardada] = useState(false);
  const [cargandoConfig, setCargandoConfig] = useState(false);

  const {
    numero = '',
    cliente = '',
    direccion = '',
    direccion_envio = '',
    telefono = '',
    items = [],
    subtotalAntesDescuentos = 0,
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    subtotal = 0,
    fecha = new Date().toISOString().split('T')[0],
    observaciones = '',
    id_empresa = null,
    empresa = '',
  } = cotizacionData;

  const configuracionResuelta = {
    ...configuracion,
    nombreEmpresa: nombreEmpresaContexto || configuracion.nombreEmpresa || '',
    direccionEmpresa: direccionEmpresaContexto || configuracion.direccionEmpresa || '',
    rncEmpresa: rncEmpresaContexto || configuracion.rncEmpresa || '',
  };

  useEffect(() => {
    if (!open) return;
    const idEmpresa = cotizacionData.id_empresa;
    const dbEmpresa = idEmpresa && typeof idEmpresa === 'object' ? idEmpresa.db_empresa : null;
    if (!dbEmpresa) {
      setConfiguracion(CONFIGURACION_DEFAULT);
      setConfiguracionGuardada(false);
      return undefined;
    }
    let cancelado = false;
    setCargandoConfig(true);
    fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: idEmpresa,
        usuario: usuarioLog || '',
        password: claveLog || '',
        tipo_documento: 'COTIZACION',
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelado) return;
        if (data.configuracion) {
          setConfiguracion({ ...CONFIGURACION_DEFAULT, ...data.configuracion });
        }
        setConfiguracionGuardada(data.configuracionGuardada === true);
      })
      .catch(() => {
        if (cancelado) return;
        setConfiguracion(CONFIGURACION_DEFAULT);
        setConfiguracionGuardada(false);
      })
      .finally(() => {
        if (!cancelado) setCargandoConfig(false);
      });
    return () => {
      cancelado = true;
    };
  }, [open, cotizacionData.id_empresa, usuarioLog, claveLog, servidor]);

  const empresaObj = typeof empresa === 'object' && empresa ? empresa : {};
  const nombreEmpresa =
    configuracionResuelta.nombreEmpresa ||
    (typeof empresa === 'string' ? empresa : empresaObj?.nombre || empresaObj?.empresa) ||
    'Empresa';
  const direccionEmpresa =
    configuracionResuelta.direccionEmpresa || empresaObj?.direccion || '';
  const telefonoEmpresa =
    configuracionResuelta.telefonoEmpresa || empresaObj?.telefono || '';
  const emailEmpresa = configuracionResuelta.emailEmpresa || empresaObj?.email || '';
  const rncEmpresa = configuracionResuelta.rncEmpresa || empresaObj?.rnc || '';
  const mostrarInfoEmpresa = configuracionResuelta.mostrarInfoEmpresa !== false;

  const handleImprimir = () => {
    if (!configuracionGuardada) {
      showError(
        'Debe configurar la plantilla de cotización en Configuración de factura (tipo COTIZACION) antes de imprimir.'
      );
      return;
    }
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    const htmlContent = generarHtmlImpresionCotizacion({
      numero,
      fecha,
      cliente,
      direccion,
      direccion_envio,
      telefono,
      items,
      subtotalAntesDescuentos,
      totalDescuentos,
      totalImpuestos,
      total,
      observaciones,
      nombreEmpresa,
      direccionEmpresa,
      telefonoEmpresa,
      rncEmpresa,
      emailEmpresa,
      mostrarInfoEmpresa,
      leyendaPiePagina: configuracionResuelta.leyendaPiePagina || '',
      mensajeGracias: configuracionResuelta.mensajeGracias || '',
    });
    ventana.document.write(htmlContent);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  useEffect(() => {
    if (open && autoPrint && configuracionGuardada && !cargandoConfig) {
      const t = setTimeout(() => handleImprimir(), 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoPrint, configuracionGuardada, cargandoConfig]);

  const abrirDialogoEmail = () => setDialogoEmailAbierto(true);
  const cerrarDialogoEmail = () => {
    setDialogoEmailAbierto(false);
    setEmailDestino('');
  };

  const handleEnviarEmail = async () => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const email = (emailDestino || '').trim();
    if (!email) {
      showError('Ingrese el correo de destino');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Correo electrónico no válido');
      return;
    }
    setEnviandoEmail(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_enviarcotizacion_email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: id_empresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          email_destino: email,
          cotizacion: {
            numero,
            cliente,
            direccion,
            direccion_envio,
            telefono,
            items,
            subtotalAntesDescuentos,
            totalDescuentos,
            totalImpuestos,
            subtotal,
            total,
            fecha,
            observaciones,
            empresa: nombreEmpresa,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.Error) {
        showSuccess('Cotización enviada por correo correctamente');
        cerrarDialogoEmail();
      } else {
        showError(data.Error || 'No se pudo enviar el correo.');
      }
    } catch {
      showError('Error de conexión al enviar el correo');
    } finally {
      setEnviandoEmail(false);
    }
  };

  const labelSx = {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: FP.color.textMuted,
    mb: 0.4,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.7rem',
    color: '#FFFFFF',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    bgcolor: FP.color.navy,
    borderBottom: 'none',
    py: 1.2,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '92vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.navySoft,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.98rem', lineHeight: 1.2 }}>
              Cotización Nº {numero || '—'}
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
              Vista previa · Formato empresarial
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface }}>
          {cargandoConfig && (
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem', mb: 2 }}>
              Cargando configuración…
            </Typography>
          )}
          {!cargandoConfig && !configuracionGuardada && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${FP.color.warning}`,
                bgcolor: FP.color.warningSoft,
              }}
            >
              Configure la plantilla de cotización en Configuración de factura (tipo COTIZACIÓN) antes de imprimir.
            </Alert>
          )}

          <Paper
            ref={printRef}
            elevation={0}
            sx={{
              bgcolor: '#FFFFFF',
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ height: 4, bgcolor: FP.color.navy }} />
            <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>

            {/* Encabezado: empresa izquierda · documento derecha */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={7}>
                {mostrarInfoEmpresa && (
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: FP.color.navy,
                        fontSize: '1.35rem',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.2,
                        mb: 0.6,
                      }}
                    >
                      {nombreEmpresa}
                    </Typography>
                    {direccionEmpresa && (
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mb: 0.2 }}>
                        {direccionEmpresa}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      {telefonoEmpresa && (
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                          Tel: {telefonoEmpresa}
                        </Typography>
                      )}
                      {rncEmpresa && (
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                          RNC: {rncEmpresa}
                        </Typography>
                      )}
                      {emailEmpresa && (
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                          {emailEmpresa}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={5}>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography sx={labelSx}>Documento</Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: FP.color.navy,
                      letterSpacing: '0.12em',
                      fontSize: '0.95rem',
                      mb: 0.4,
                    }}
                  >
                    COTIZACIÓN
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: FP.color.textPrimary,
                      fontSize: '1.35rem',
                      fontVariantNumeric: 'tabular-nums',
                      mb: 0.4,
                    }}
                  >
                    Nº {numero || '—'}
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                    {formatoFechaLargo(fecha)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Bloque cliente */}
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                borderRadius: 1.5,
                bgcolor: FP.color.surfaceAlt,
                border: `1px solid ${FP.color.border}`,
              }}
            >
              <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 1 }}>Cliente</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem', mb: 0.4 }}>
                    {cliente || '—'}
                  </Typography>
                  {direccion && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                      {direccion}
                    </Typography>
                  )}
                  {telefono && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mt: 0.3 }}>
                      Tel: {telefono}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {direccion_envio && (
                    <>
                      <Typography sx={labelSx}>Entrega / Envío</Typography>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                        {direccion_envio}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>

            {/* Detalle */}
            <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 1 }}>Detalle</Typography>
            <TableContainer sx={{ borderRadius: 1.5, border: `1px solid ${FP.color.border}`, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={cellHeadSx}>Código</TableCell>
                    <TableCell sx={cellHeadSx}>Descripción</TableCell>
                    <TableCell align="center" sx={cellHeadSx}>
                      Cant.
                    </TableCell>
                    <TableCell align="right" sx={cellHeadSx}>
                      Precio
                    </TableCell>
                    <TableCell align="right" sx={cellHeadSx}>
                      Desc.
                    </TableCell>
                    <TableCell align="right" sx={cellHeadSx}>
                      Subtotal
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!items || items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: FP.color.textMuted, py: 3 }}>
                        Sin ítems
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, idx) => (
                      <TableRow
                        key={item.id || idx}
                        sx={{ bgcolor: idx % 2 === 0 ? '#FFFFFF' : FP.color.surfaceAlt }}
                      >
                        <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                          {item.codigo || '—'}
                        </TableCell>
                        <TableCell sx={{ color: FP.color.textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>
                          {item.descripcion || '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                          {parseFloat(item.cantidad || 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
                          {money(item.precio)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
                          {money(item.descuento)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {money(item.subtotal)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totales */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
              <Box
                sx={{
                  width: { xs: '100%', sm: 280 },
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 1.8,
                    py: 1,
                    borderBottom: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>Subtotal</Typography>
                  <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
                    {money(subtotalAntesDescuentos)}
                  </Typography>
                </Box>
                {totalDescuentos > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      px: 1.8,
                      py: 1,
                      borderBottom: `1px solid ${FP.color.border}`,
                    }}
                  >
                    <Typography sx={{ color: FP.color.danger, fontSize: '0.82rem' }}>Descuentos</Typography>
                    <Typography sx={{ color: FP.color.danger, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
                      −{money(totalDescuentos)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 1.8,
                    py: 1,
                    borderBottom: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>Impuestos</Typography>
                  <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
                    {money(totalImpuestos)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1.8,
                    py: 1.4,
                    bgcolor: FP.color.navy,
                  }}
                >
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.06em' }}>
                    TOTAL
                  </Typography>
                  <Typography
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 800,
                      fontSize: '1.15rem',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {money(total)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {observaciones && (
              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 0.8 }}>Observaciones</Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                  {observaciones}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                mt: 3,
                pt: 1.5,
                borderTop: `1px solid ${FP.color.border}`,
                textAlign: 'center',
              }}
            >
              {configuracionResuelta.mensajeGracias && (
                <Typography sx={{ color: FP.color.navy, fontWeight: 700, fontSize: '0.85rem', mb: 0.4 }}>
                  {configuracionResuelta.mensajeGracias}
                </Typography>
              )}
              <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
                Este documento es una cotización y no constituye una factura fiscal.
              </Typography>
              <Typography sx={{ color: FP.color.textMuted, fontSize: '0.7rem', mt: 0.3 }}>
                Emitida el {formatoFechaCorto(fecha)}
              </Typography>
            </Box>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            disabled={cargandoConfig || !configuracionGuardada}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              '&.Mui-disabled': { color: FP.color.textMuted },
            }}
          >
            {cargandoConfig ? 'Cargando formato…' : 'Imprimir'}
          </Button>
          <Button
            startIcon={<EmailIcon />}
            onClick={abrirDialogoEmail}
            variant="outlined"
            disabled={!usuarioLog}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              '&.Mui-disabled': { color: FP.color.textMuted },
            }}
          >
            Enviar por email
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoEmailAbierto}
        onClose={cerrarDialogoEmail}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            fontWeight: 700,
            color: FP.color.textPrimary,
            fontSize: '0.98rem',
          }}
        >
          Enviar cotización por correo
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <TextField
            autoFocus
            fullWidth
            label="Correo electrónico"
            type="email"
            value={emailDestino}
            onChange={(e) => setEmailDestino(e.target.value)}
            placeholder="ejemplo@correo.com"
            disabled={enviandoEmail || !usuarioLog}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1, ...fpInputSx }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogoEmail}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnviarEmail}
            variant="contained"
            disabled={enviandoEmail || !usuarioLog}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {enviandoEmail ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VistaCotizacion;
